import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { SwapVertRounded } from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { Block, Button } from "../../../shared";
import { CryptoBadge } from "./CryptoBadge";
import { CryptoPickerDrawer } from "./CryptoPickerDrawer";
import { formatAmount } from "./format";
import {
  executeSwap,
  getSwapQuote,
  listSwapPairs,
  SwapPair,
  SwapQuote,
} from "../../../../services/swap";

// ================================================================
// Виджет кросс-чейн обмена крипта↔крипта (SwapKit под капотом).
//
// Списки пар и лимитов приходят с бэка (админ настраивает в
// /admin → «Обменник»). Каждая пара — уникальная связка
// `fromCurrency+fromNetwork → toCurrency+toNetwork` с конкретным
// SwapKit-роутом. Пользователь выбирает пару, вводит сумму, видит
// live-курс и кнопку «Обменять».
// ================================================================

function sanitizeAmountInput(raw: string): string {
  let s = raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
  if (s.startsWith(".")) s = "0" + s;
  return s;
}

type PickerSide = "from" | "to" | null;

export const CryptoSwapWidget: React.FC = () => {
  const theme = useTheme();
  const qc = useQueryClient();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation("exchange");

  const { data: pairs = [], isLoading: pairsLoading } = useQuery({
    queryFn: () => listSwapPairs(),
    queryKey: ["swap-pairs"],
  });

  // Уникальные комбинации (currency, network) — как «валюты» в UI.
  const fromOptions = useMemo(() => {
    const map = new Map<string, { currency: string; network: string }>();
    pairs.forEach((p) => {
      const key = `${p.from.currency}:${p.from.network}`;
      if (!map.has(key)) map.set(key, p.from);
    });
    return Array.from(map.values());
  }, [pairs]);

  const [from, setFrom] = useState<{ currency: string; network: string } | null>(
    null,
  );
  const [to, setTo] = useState<{ currency: string; network: string } | null>(
    null,
  );

  useEffect(() => {
    if (!from && fromOptions[0]) setFrom(fromOptions[0]);
  }, [fromOptions, from]);

  const toOptions = useMemo(() => {
    if (!from) return [];
    const opts = pairs
      .filter(
        (p) =>
          p.from.currency === from.currency && p.from.network === from.network,
      )
      .map((p) => p.to);
    const seen = new Set<string>();
    return opts.filter((o) => {
      const k = `${o.currency}:${o.network}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [pairs, from]);

  useEffect(() => {
    if (
      from &&
      (!to ||
        !toOptions.some(
          (o) => o.currency === to.currency && o.network === to.network,
        ))
    ) {
      setTo(toOptions[0] ?? null);
    }
  }, [from, to, toOptions]);

  const currentPair: SwapPair | null = useMemo(() => {
    if (!from || !to) return null;
    return (
      pairs.find(
        (p) =>
          p.from.currency === from.currency &&
          p.from.network === from.network &&
          p.to.currency === to.currency &&
          p.to.network === to.network,
      ) ?? null
    );
  }, [pairs, from, to]);

  // Пользовательский ввод — для реального execute.
  const [sellAmount, setSellAmount] = useState("");
  // Live-quote для конкретной введённой суммы (используется в execute).
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  // Индикативный курс для пары — заполняется при выборе from/to без
  // ввода суммы. Обновляется автоматически, чтобы юзер всегда видел
  // «где рынок». Показываем и обратный курс.
  const REFRESH_INTERVAL_MS = 5_000;
  const indicativeQuery = useQuery({
    queryKey: [
      "swap-indicative",
      currentPair?.id,
      currentPair?.min_amount ?? "1",
    ],
    queryFn: async () => {
      if (!currentPair) return null;
      // Берём min_amount как пробную сумму — если его нет, "1".
      const trial = currentPair.min_amount ?? "1";
      return getSwapQuote(currentPair.id, trial);
    },
    enabled: !!currentPair,
    // Освежаем часто, чтобы курс не «протухал».
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: REFRESH_INTERVAL_MS - 500,
    // Умный ретрай: клиентские 4xx (в т.ч. 422 «нет маршрута»)
    // не имеют смысла ретраить — ответ не изменится. Ретраим только
    // сетевые/серверные сбои и делаем это один раз.
    retry: (failureCount, err: any) => {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  // Сообщение об ошибке индикативного курса (для случая, когда юзер
  // не вводил сумму — quoteError остаётся пустым). Достаём читаемый
  // message с бэка.
  const indicativeErrorMsg = useMemo(() => {
    if (!indicativeQuery.isError) return null;
    const e: any = indicativeQuery.error;
    return (
      e?.response?.data?.message ||
      e?.response?.data?.detail ||
      e?.message ||
      t("rate_unavailable")
    );
  }, [indicativeQuery.isError, indicativeQuery.error, t]);

  const displayQuote = quote ?? indicativeQuery.data ?? null;
  const rate = displayQuote ? Number(displayQuote.rate) : null;
  const inverseRate = rate && rate > 0 ? 1 / rate : null;

  const buyAmountDisplay = quote
    ? formatAmount(quote.buy_amount)
    : sellAmount && rate
      ? formatAmount(Number(sellAmount) * rate)
      : "";

  const runQuote = useMemo(
    () =>
      debounce(async (pairId: string, amount: string) => {
        setQuoting(true);
        setQuoteError(null);
        try {
          const q = await getSwapQuote(pairId, amount);
          setQuote(q);
        } catch (e: any) {
          setQuote(null);
          const msg =
            e?.response?.data?.message ||
            e?.response?.data?.detail ||
            e?.message ||
            t("errors.quote_failed");
          setQuoteError(msg);
        } finally {
          setQuoting(false);
        }
      }, 500),
    [],
  );

  useEffect(() => {
    if (!currentPair || !sellAmount || Number(sellAmount) <= 0) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    runQuote(currentPair.id, sellAmount);
  }, [currentPair, sellAmount, runQuote]);

  useEffect(() => () => runQuote.cancel(), [runQuote]);

  const [pickerSide, setPickerSide] = useState<PickerSide>(null);
  const openPicker = (side: "from" | "to") => () => setPickerSide(side);
  const closePicker = () => setPickerSide(null);

  const handleSelect = (
    side: "from" | "to",
    currency: string,
    network: string,
  ) => {
    if (side === "from") setFrom({ currency, network });
    else setTo({ currency, network });
    setQuote(null);
  };

  const handleSwap = () => {
    if (!from || !to) return;
    const inverse = pairs.find(
      (p) =>
        p.from.currency === to.currency &&
        p.from.network === to.network &&
        p.to.currency === from.currency &&
        p.to.network === from.network,
    );
    if (!inverse) {
      enqueueSnackbar(t("no_reverse_pair"), { variant: "info" });
      return;
    }
    setFrom(to);
    setTo(from);
    setSellAmount("");
    setQuote(null);
  };

  const executeMutation = useMutation({
    mutationFn: () => {
      if (!currentPair || !quote) {
        throw new Error(t("errors.no_active_quote"));
      }
      return executeSwap({
        pairId: currentPair.id,
        routeId: quote.route_id,
        sellAmount: quote.sell_amount,
        minBuyAmount: quote.buy_amount,
      });
    },
    onSuccess: () => {
      enqueueSnackbar(t("success_full"), { variant: "success" });
      setSellAmount("");
      setQuote(null);
      qc.invalidateQueries(["swap-orders"]);
      qc.invalidateQueries(["crypto-overview"]);
      qc.invalidateQueries(["balances"]);
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        t("errors.swap_failed");
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const canExecute =
    !!quote &&
    !!currentPair &&
    !quoting &&
    !executeMutation.isLoading &&
    Number(sellAmount) > 0 &&
    !quoteError;

  if (pairsLoading) {
    return (
      <Block sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
        <CircularProgress size={22} />
      </Block>
    );
  }

  if (!pairs.length) {
    return (
      <Block sx={{ padding: "16px" }}>
        <Typography sx={{ textAlign: "center", opacity: 0.6 }}>
          {t("unavailable_hint")}
        </Typography>
      </Block>
    );
  }

  const inputFontSize =
    sellAmount.length > 12
      ? "1.7rem"
      : sellAmount.length > 8
        ? "2.2rem"
        : "3rem";
  const buyFontSize =
    buyAmountDisplay.length > 12
      ? "1.7rem"
      : buyAmountDisplay.length > 8
        ? "2.2rem"
        : "3rem";

  return (
    <Block
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "10px 16px 14px",
        position: "relative",
        zIndex: 999,
      }}
    >
      {/* From */}
      <Box sx={{ display: "flex", alignItems: "center", marginTop: "6px" }}>
        <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, opacity: 0.65, mb: 0.5 }}>
            {t("you_send")}
          </Typography>
          <TextField
            placeholder="0"
            inputMode="decimal"
            value={sellAmount}
            onChange={(e) => setSellAmount(sanitizeAmountInput(e.target.value))}
            variant="outlined"
            sx={{
              width: "100%",
              input: {
                border: "none",
                fontSize: inputFontSize,
                fontWeight: 500,
                padding: "0",
                lineHeight: 1.1,
                transition: "font-size 0.12s ease-out",
              },
              "& fieldset": { border: "none" },
            }}
          />
          {currentPair?.min_amount && (
            <Typography sx={{ fontSize: 11.5, opacity: 0.55, mt: 0.25 }}>
              {currentPair.max_amount
                ? t("min_max_full", {
                    min: formatAmount(currentPair.min_amount),
                    code: from?.currency ?? "",
                    max: formatAmount(currentPair.max_amount),
                  })
                : t("min_max", {
                    min: formatAmount(currentPair.min_amount),
                    code: from?.currency ?? "",
                  })}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
            ml: 1,
          }}
          onClick={openPicker("from")}
        >
          {from && (
            <CryptoBadge currency={from.currency} network={from.network} />
          )}
          <ArrowForwardIosIcon
            sx={{ fontSize: 16, color: "rgba(140, 140, 141, 1)", ml: 0.5 }}
          />
        </Box>
      </Box>

      {/* Middle (divider + swap button) */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          my: 1.5,
        }}
      >
        <Divider
          sx={{
            borderColor: isDark ? "#26262A" : "#E8E8ED",
            flex: 1,
            margin: "0 -16px",
          }}
        />
        <IconButton
          sx={{
            boxShadow:
              "-0.5px 0.5px 1px 0 rgba(0, 0, 0, 0.30) inset, 0.5px -0.5px 1px 0 rgba(0, 0, 0, 0.30) inset, 0.5px 0.5px 1px 0 rgba(255, 255, 255, 0.30) inset, -0.5px -0.5px 1px 0 rgba(255, 255, 255, 0.30) inset",
            filter: "drop-shadow(0 5px 10px rgba(0, 0, 0, 0.20))",
            backdropFilter: "blur(5px)",
            position: "absolute",
            right: "16px",
            backgroundColor: "rgba(75, 75, 78, 0.40)",
            border: `1px solid ${isDark ? "#26262A" : "#E8E8ED"}`,
            ":hover": { backgroundColor: "rgba(75, 75, 78, 0.40)" },
          }}
          onClick={handleSwap}
        >
          <SwapVertRounded color="primary" />
        </IconButton>
      </Box>

      {/* To */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, opacity: 0.65, mb: 0.5 }}>
            {t("you_get")}
          </Typography>
          <TextField
            placeholder="0"
            value={buyAmountDisplay}
            variant="outlined"
            slotProps={{ htmlInput: { readOnly: true } }}
            sx={{
              width: "100%",
              input: {
                border: "none",
                fontSize: buyFontSize,
                fontWeight: 500,
                padding: "0",
                lineHeight: 1.1,
                opacity: quoting ? 0.5 : 1,
                transition: "font-size 0.12s ease-out, opacity 0.2s",
              },
              "& fieldset": { border: "none" },
            }}
          />
          {/* Сообщение об ошибке курса теперь показывается
              в RateInfoPanel — здесь дублировать не нужно. */}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
            ml: 1,
          }}
          onClick={openPicker("to")}
        >
          {to && <CryptoBadge currency={to.currency} network={to.network} />}
          <ArrowForwardIosIcon
            sx={{ fontSize: 16, color: "rgba(140, 140, 141, 1)", ml: 0.5 }}
          />
        </Box>
      </Box>

      {/* ================================================================
          Плашка с деталями: курс (обе стороны), время, комиссия сети.
          Показывается всегда когда есть indicative-quote или live-quote.
          ================================================================ */}
      <RateInfoPanel
        from={from?.currency ?? null}
        to={to?.currency ?? null}
        rate={rate}
        inverseRate={inverseRate}
        timeEstimateSec={displayQuote?.time_estimate_sec ?? null}
        networkFee={displayQuote?.fees?.total ?? null}
        isRefreshing={indicativeQuery.isFetching || quoting}
        isIndicative={!quote && !!indicativeQuery.data}
        isDark={isDark}
        errorMsg={
          !rate && !indicativeQuery.isFetching
            ? (quoteError ?? indicativeErrorMsg)
            : null
        }
        // Оранжевый таймер отсчёта до следующего рефреша курса.
        refreshIntervalMs={REFRESH_INTERVAL_MS}
        lastUpdatedAt={indicativeQuery.dataUpdatedAt}
        t={t}
      />

      <Button
        sx={{ marginTop: "12px" }}
        disabled={!canExecute}
        onClick={() => executeMutation.mutate()}
      >
        {executeMutation.isLoading
          ? t("confirming")
          : quoting
            ? t("quoting")
            : t("confirm")}
      </Button>

      {/* Полноэкранный селектор валюты + сети */}
      <CryptoPickerDrawer
        open={pickerSide === "from"}
        onClose={closePicker}
        title={t("picker.from_title")}
        subtitle={t("picker.from_subtitle")}
        options={fromOptions}
        selected={from}
        onSelect={(opt) => handleSelect("from", opt.currency, opt.network)}
      />
      <CryptoPickerDrawer
        open={pickerSide === "to"}
        onClose={closePicker}
        title={t("picker.to_title")}
        subtitle={t("picker.to_subtitle")}
        options={toOptions}
        selected={to}
        onSelect={(opt) => handleSelect("to", opt.currency, opt.network)}
      />
    </Block>
  );
};

// ================================================================
// Информационная плашка курса
// ================================================================

interface RateInfoPanelProps {
  from: string | null;
  to: string | null;
  rate: number | null;
  inverseRate: number | null;
  timeEstimateSec: number | null;
  networkFee: string | null;
  isRefreshing: boolean;
  isIndicative: boolean;
  isDark: boolean;
  errorMsg?: string | null;
  refreshIntervalMs: number;
  lastUpdatedAt: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

// Оранжевый — брендовый акцент для «живого» рефреша курса.
const ACCENT_ORANGE = "#F59E0B";

// Мини-компонент: кольцевой таймер обратного отсчёта, который заполняется
// от 0 до 100% за `intervalMs` мс с момента `lastUpdatedAt`. Пока
// `isRefreshing = true` — крутится indeterminate спиннер того же цвета.
function RefreshCountdown({
  intervalMs,
  lastUpdatedAt,
  isRefreshing,
  size = 16,
  thickness = 3.5,
}: {
  intervalMs: number;
  lastUpdatedAt: number;
  isRefreshing: boolean;
  size?: number;
  thickness?: number;
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isRefreshing) return;
    let raf = 0;
    const tick = () => {
      const elapsed = Date.now() - (lastUpdatedAt || Date.now());
      const p = Math.min(100, (elapsed / intervalMs) * 100);
      setProgress(p);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [intervalMs, lastUpdatedAt, isRefreshing]);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
      }}
    >
      {/* Фоновая дорожка */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: ACCENT_ORANGE, opacity: 0.18, position: "absolute" }}
      />
      {/* Активная часть */}
      {isRefreshing ? (
        <CircularProgress
          size={size}
          thickness={thickness}
          sx={{ color: ACCENT_ORANGE, position: "absolute" }}
        />
      ) : (
        <CircularProgress
          variant="determinate"
          value={progress}
          size={size}
          thickness={thickness}
          sx={{
            color: ACCENT_ORANGE,
            position: "absolute",
            transition: "none",
            "& .MuiCircularProgress-circle": {
              transition: "stroke-dashoffset 0.08s linear",
            },
          }}
        />
      )}
    </Box>
  );
}

function RateInfoPanel({
  from,
  to,
  rate,
  inverseRate,
  timeEstimateSec,
  networkFee,
  isRefreshing,
  isIndicative,
  isDark,
  errorMsg,
  refreshIntervalMs,
  lastUpdatedAt,
  t,
}: RateInfoPanelProps) {
  // Если пары ещё не выбраны — вообще ничего не показываем.
  if (!from || !to) return null;

  // Пара выбрана, но курс не пришёл (или ещё грузится с ошибкой).
  // Показываем компактный статус, чтобы юзер понимал что происходит.
  if (!rate) {
    if (!errorMsg && !isRefreshing) return null;
    return (
      <Box
        sx={{
          mt: 1.5,
          borderRadius: "16px",
          padding: "10px 12px",
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {isRefreshing ? (
          <>
            <CircularProgress size={12} sx={{ opacity: 0.6 }} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {t("fetching_rate")}
            </Typography>
          </>
        ) : (
          <>
            <Icon
              icon="solar:info-circle-linear"
              width={14}
              height={14}
              style={{ opacity: 0.7 }}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {errorMsg ?? t("rate_unavailable")}
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: "16px",
        padding: "10px 12px",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      {/* Курсы — две строки */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          1 {from} ≈ {formatAmount(rate)} {to}
        </Typography>
        <RefreshCountdown
          intervalMs={refreshIntervalMs}
          lastUpdatedAt={lastUpdatedAt}
          isRefreshing={isRefreshing}
        />
      </Box>
      {inverseRate ? (
        <Typography
          sx={{
            fontSize: 12,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          1 {to} ≈ {formatAmount(inverseRate)} {from}
        </Typography>
      ) : null}

      {/* Мета-строка: время + комиссия + метка indicative */}
      {(timeEstimateSec || networkFee || isIndicative) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            mt: 0.5,
            flexWrap: "wrap",
          }}
        >
          {timeEstimateSec ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "text.secondary",
                fontSize: 11.5,
              }}
            >
              <Icon icon="solar:clock-circle-linear" width={13} height={13} />
              <span>
                {t("time_estimate_min", {
                  min: Math.max(1, Math.round(timeEstimateSec / 60)),
                })}
              </span>
            </Box>
          ) : null}
          {networkFee ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "text.secondary",
                fontSize: 11.5,
              }}
            >
              <Icon icon="solar:tag-linear" width={13} height={13} />
              <span>
                {t("network_fee_short", { fee: formatAmount(networkFee) })}
              </span>
            </Box>
          ) : null}
          {isIndicative ? (
            <Box
              sx={{
                fontSize: 11,
                color: "text.secondary",
                opacity: 0.6,
                ml: "auto",
              }}
            >
              {t("indicative")}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
